/**
 * Boot, the build cursor, then the frame loop.
 *
 * The order matters and is the point of B0.1: diagnose the platform first, put
 * the sim in a worker second, and only then let anything draw. If WebGPU is
 * missing the page says so in words and stops — there is no WebGL2 path to fall
 * into (docs/13-architecture.md).
 *
 * B0.2 adds the pointer: hover a cell, ask the sim whether the edit would be
 * legal, tint the ghost with the answer, and on click send the edit as a
 * *command*. The asking happens when the cursor changes cell — a player
 * action's cadence — and never per frame; the answer is the sim's own, so the
 * cursor and the click can never disagree about the rules.
 *
 * The loop below is the seam under load, end to end: read the two newest
 * snapshots, interpolate, draw, measure, and decimate the measurements into the
 * React store. Every number the HUD shows is taken here.
 */

import { reportBootFailure, runBench } from './bench';
import { FloorHost, preferredTransport, type Command, type FloorCommand, type FloorUi } from './bridge';
import { environmentReport, renderBootFailure } from './boot';
import { FloorRenderer, type CursorGhost } from './engine/floor';
import { mountHud } from './ui/mount';
import { buildState, pendingRect, setBuild, type BuildMode, type BuildState, type MovingTool } from './ui/build';
import { noteLoad, noteSave, recordCampaign, recordTick, startPlaytest } from './ui/playtest';
import { clearAutosave, downloadSave, pickSaveFile, readAutosave, writeAutosave, type SaveStore } from './ui/saves';
import type { CampaignActions } from './ui/TopBar';
import { flush, snapshot, update } from './ui/store';

/**
 * Agents the world has room for. The crew dial goes up to this, and the shift
 * the floor *boots* with is content rather than a number here: the era's
 * catalog authors it (B0.4).
 */
const CAPACITY = 4096;
/** Same seed the `floor` harness binary runs, so hashes are comparable by eye. */
const SEED = 0x0b010001;
/** The economy's RNG seed: the launch seed's own default (M1.1). `?rng=` overrides. */
const RNG_SEED = 1974;
/** Autosave cadence: every so many calendar weeks, and every so long in the chair. */
const AUTOSAVE_WEEKS = 4;
const AUTOSAVE_MS = 60_000;
/** How often the HUD is allowed to re-render, in milliseconds. */
const HUD_MS = 200;

/**
 * The platform matrix's probe: the build reporting on itself, readable from a
 * devtools console or from an automated driver. Every cell of the matrix needs
 * an answer, and half the cells are browsers and webviews nobody can script
 * (docs/14-roadmap.md, B0.1 and B0.6).
 */
declare global {
  interface Window {
    yieldProbe: () => unknown;
    /**
     * The command queue and the layout file, reachable from a console or a
     * driver. `yieldProbe` reads; this one *acts* — which is what lets a
     * matrix cell be filled by an export → clear → import round trip rather
     * than by a person with a mouse. It sends the same commands the UI does,
     * through the same queue: there is no privileged path in here.
     */
    yieldDrive: {
      send: (command: FloorCommand) => void;
      /** M1.1: an economy command, and the campaign's save/load, from a console. */
      sendSim: (command: Command) => void;
      exportLayout: () => Promise<string>;
      save: () => Promise<string>;
      load: (json: string) => Promise<string | null>;
    };
  }
}
window.yieldProbe = () => {
  const state = snapshot();
  const flow = state.ui?.flow;
  const crew = state.ui?.crew;
  return {
    ...state,
    // The build cursor's own state, so a driver can see what the sim answered
    // about the pending edit without reading pixels.
    cursor: {
      mode: buildState().mode,
      cell: buildState().cursor,
      rect: pendingRect(buildState()),
      verdict: buildState().verdict,
      asking: buildState().asking,
    },
    // And the flow, flattened: a driver checking that WIP circulates and a
    // queue piles should not have to walk the store to find out.
    flow: flow
      ? {
          startsPerDay: flow.startsPerDay,
          released: flow.released,
          completed: flow.completed,
          inFlight: flow.inFlight,
          stuck: flow.stuck,
          hotLots: flow.hotLots,
          cycleHours: Number(flow.meanCycleHoursAll.toFixed(2)),
          xFactor: Number(flow.cycleRatio.toFixed(2)),
          bottleneck: flow.bottleneck
            ? {
                name: flow.bottleneck.name,
                mille: flow.bottleneck.utilizationMille,
                // Its cell, so a driver can aim a click at the tool the HUD is
                // pointing at rather than at the middle of the floor.
                x: flow.bottleneck.x,
                y: flow.bottleneck.y,
              }
            : null,
          deepestQueue: flow.tools.reduce((most, tool) => Math.max(most, tool.queueLots), 0),
          // B0.4: what the people cost, flattened for a driver.
          transitHours: Number(flow.transitHours.toFixed(2)),
          transitShare: flow.transitShareMille,
          awaitingCarriage: flow.awaitingCarriage,
          starvedTools: flow.starvedTools,
        }
      : null,
    // B0.5: the seam itself, flattened. A driver checking that fast-forward
    // agrees with the watched floor reads these two beside each other rather
    // than walking the curve.
    calibration: state.ui
      ? {
          status: state.ui.calibration.status,
          ready: state.ui.calibration.ready,
          windows: state.ui.calibration.windows,
          resets: state.ui.calibration.resets,
          points: state.ui.calibration.points.length,
          signature: state.ui.calibration.signature,
          capacityWafersPerWeek: state.ui.calibration.capacityWafersPerWeek,
          forecastWafersPerWeek: state.ui.calibration.forecastWafersPerWeek,
          forecastCycleHours: Number(state.ui.calibration.forecastCycleHours.toFixed(2)),
          forecastD0: state.ui.calibration.forecastD0Mille,
          liveD0: state.ui.calibration.liveD0Mille,
          floorView: state.ui.floorView,
        }
      : null,
    // B0.7: the score, flattened. A driver — or a person reading a console at
    // the end of a sitting — should be able to ask "how did that fab do" without
    // walking the store, and the session counters are the sitting's own log.
    score: state.ui
      ? {
          objective: state.ui.score.objective?.id ?? null,
          settled: state.ui.score.settled,
          wafersPerWeek: state.ui.score.wafersPerWeek,
          cycleHours: Number(state.ui.score.cycleHours.toFixed(2)),
          costPerWafer: Number(state.ui.score.costPerWaferDollars.toFixed(2)),
          weeklyCost: Math.round(state.ui.score.cost.totalDollars),
          met: state.ui.score.met,
          bottleneck: state.ui.score.bottleneck,
          dispatch: state.ui.score.dispatch,
          moves: state.ui.score.moves.length,
          lastMove: state.ui.score.moves[0]
            ? {
                label: state.ui.score.moves[0].label,
                note: state.ui.score.moves[0].note,
                deltaWafers: state.ui.score.moves[0].deltaWafers,
                // Nullish: an absent `Option` crosses as `undefined`.
                settled: state.ui.score.moves[0].after != null,
              }
            : null,
          session: state.ui.score.session,
        }
      : null,
    // M1.1: the campaign, flattened — where the calendar is, how it is being
    // carried, what the firm has, and the glued hash a driver can compare
    // against `campaign parity`.
    campaign: state.campaign
      ? {
          week: state.campaign.week,
          date: state.campaign.date,
          run: state.campaign.run,
          pace: state.campaign.pace,
          watchedWeeks: state.campaign.watchedWeeks,
          forwardedWeeks: state.campaign.forwardedWeeks,
          hash: state.campaign.hash,
          cash: Math.round(state.campaign.firm.cash),
          debt: Math.round(state.campaign.firm.debt),
          lines: state.campaign.firm.lines.map((l) => ({
            id: l.id,
            source: l.source,
            startsPerDay: l.startsPerDay,
            wafersOut: l.wafersOut,
            booked: l.bookedWafersOut,
            observed: l.bookedObserved,
          })),
          siteCurveStale: state.campaign.siteCurveStale,
        }
      : null,
    // And the crew itself, so a matrix cell can record that the floor was
    // inhabited and how hard it was working without reading pixels.
    crew: crew
      ? {
          agents: crew.agents,
          working: crew.carrying + crew.loading + crew.repairing,
          walking: crew.walking,
          resting: crew.resting,
          gowning: crew.gowning,
          idle: crew.idle,
          fatigue: crew.fatigueMille,
          discipline: crew.disciplineMille,
          contamination: crew.contamination,
          paceMille: crew.paceMille,
        }
      : null,
  };
};

void start();

async function start(): Promise<void> {
  const bootHost = document.getElementById('boot') as HTMLElement;
  const params = new URLSearchParams(window.location.search);
  const report = await environmentReport();
  update({ report, isolated: window.crossOriginIsolated });
  // The sitting's clock starts before the first tick does (B0.7): the sim keeps
  // theater time, and real time is the shell's to measure.
  startPlaytest();

  if (!report.webgpu.ok) {
    renderBootFailure(bootHost, report);
    // A cell that does not boot still owes the matrix an answer (B0.6).
    if (params.has('bench')) void reportBootFailure(report);
    return;
  }

  const transport = preferredTransport();
  const rngSeed = Number(params.get('rng') ?? RNG_SEED) || RNG_SEED;
  const host = new FloorHost({ seed: SEED, rngSeed, capacity: CAPACITY, transport });
  const saves = new SaveKeeper(host);
  host.onTick = (ui, campaign, worker) => {
    update({ ui, campaign, worker });
    recordCampaign(campaign);
    saves.observe(campaign.week, campaign.run);
    // B0.7: the relocate cursor takes hold of whatever machine the *sim* has
    // selected. What a thing is comes from the per-tick store, never from the
    // snapshot the renderer reads (docs/13-architecture.md).
    const held = buildState().moving;
    const moving = movingTool(ui.site.selection);
    // A machine just picked up should keep facing the way it faces, so `r`
    // turns it from where it stands rather than from an arbitrary default.
    if (moving && moving.id !== held?.id) setBuild({ moving, rotated: moving.rotated });
    else setBuild({ moving });
    recordTick(ui);
  };
  host.onError = (error) => update({ error });

  window.yieldDrive = {
    send: (command) => host.send(command),
    sendSim: (command) => host.sendSim(command),
    exportLayout: () => host.exportLayout(),
    save: () => host.save(),
    load: (json) => host.load(json),
  };

  const ready = await host.ready;
  // The autosave, if there is one, unless the page was asked for a fresh
  // campaign (`?fresh=1`) or booted for the bench.
  if (!params.has('fresh') && !params.has('bench')) await saves.resume();
  update({
    transport: ready.transport,
    tickHz: ready.tickHz,
    budget: ready.budget,
    wasmMemoryBytes: ready.wasmMemoryBytes,
    ringBytes: ready.layout.ringBytes,
    catalog: ready.catalog,
  });
  setBuild({ catalog: ready.catalog });

  const canvas = document.getElementById('floor') as HTMLCanvasElement;
  const renderer = new FloorRenderer(
    canvas,
    ready.layout.capacity,
    ready.layout.structureCapacity,
    ready.layout.lotCapacity,
    ready.catalog,
  );
  try {
    await renderer.init();
  } catch (error) {
    const failed = {
      ...report,
      webgpu: {
        ok: false as const,
        reason: 'WebGPU adapter exists but the renderer failed to start.',
        detail: String(error),
      },
    };
    renderBootFailure(bootHost, failed);
    // The most interesting failure the matrix can record: an adapter that
    // exists and a pipeline that will not build. B0.4 moved the crowd onto a
    // compute pass, and a webview that boots a render pipeline does not thereby
    // boot a compute one.
    if (params.has('bench')) void reportBootFailure(failed);
    host.shutdown();
    return;
  }

  // The LOD tiers are on unless the bench turns them off to price them
  // (B0.6). Not a quality setting: it is a control case.
  renderer.setLod(params.get('lod') !== 'off');

  window.addEventListener('resize', () => renderer.resize());
  wireCursor(canvas, host, renderer);
  // The overlays' one line to the renderer: a cell to look at, or `null` for
  // the whole plot. Nothing per-frame crosses here (docs/13-architecture.md).
  mountHud(host, (cell) => (cell ? renderer.focus(cell.x, cell.y) : renderer.resetView()), saves.actions());
  loop(host, renderer);

  // `?bench=1` turns the page into its own instrument. Every matrix cell —
  // including the webviews and the browsers nobody can script — is filled by
  // opening a URL and waiting (B0.6, `web/scripts/bench.mjs`).
  if (params.has('bench')) void runBench(host, params);
}

/**
 * The build/inspect cursor. Two interaction families over one canvas
 * (docs/11-interface.md): inspect clicks *select*, build clicks *edit*, and
 * both leave as commands.
 */
function wireCursor(canvas: HTMLCanvasElement, host: FloorHost, renderer: FloorRenderer): void {
  // The last question asked, so the same one is not asked sixty times a second.
  let askedFor = '';
  let asking = 0;

  const askAbout = (state: BuildState) => {
    const command = pendingCommand(state);
    const key = command ? JSON.stringify(command) : '';
    if (key === askedFor) return;
    askedFor = key;
    if (!command) {
      setBuild({ verdict: null, asking: false });
      return;
    }
    const ticket = ++asking;
    setBuild({ asking: true });
    void host.probe(command).then((refusal) => {
      // A stale answer is worse than none: the cursor has moved on.
      if (ticket !== asking) return;
      setBuild({ verdict: refusal, asking: false });
    });
  };

  const track = (event: PointerEvent) => {
    // Two kinds of aiming (M1.2): modes that point at *things* — inspect, a
    // demolition, picking a machine up — resolve the cuboid under the
    // pointer, so a click on a furnace's body means the furnace and not the
    // floor cell hiding behind it. Placement modes keep the ground plane,
    // because there you are aiming at floor.
    const state = buildState();
    const ground = renderer.cellAt(event.clientX, event.clientY);
    const cell = aimsAtThings(state)
      ? renderer.pickAt(event.clientX, event.clientY) ?? ground
      : ground;
    const plot = renderer.plot();
    const inside = cell.x >= 0 && cell.y >= 0 && cell.x < plot.w && cell.y < plot.h;
    setBuild({ cursor: inside ? cell : null });
    askAbout(buildState());
  };

  canvas.addEventListener('pointermove', track);
  canvas.addEventListener('pointerleave', () => setBuild({ cursor: null, anchor: null }));

  canvas.addEventListener('pointerdown', (event) => {
    track(event);
    const state = buildState();
    if (!state.cursor) return;
    // A room is dragged out; everything else is a single click.
    if (state.mode.kind === 'room') {
      setBuild({ anchor: state.cursor });
      canvas.setPointerCapture(event.pointerId);
      return;
    }
    // Shift-click in inspect mode is the hot-lot lever: it aims at whatever lot
    // is under the pointer rather than at the tool it is standing on.
    // A used machine is bought through the campaign, which resolves the
    // second-hand market's price and moves the pool (M1.2); everything else
    // is a floor command as it always was.
    if (state.mode.kind === 'tool' && state.mode.used) {
      host.sendCampaign({
        kind: 'buyUsed',
        tool: state.mode.typeId,
        model: state.mode.modelId ?? '',
        x: state.cursor.x,
        y: state.cursor.y,
        rotated: state.rotated,
      });
      return;
    }
    const command =
      pendingCommand(state) ??
      (event.shiftKey ? expediteCommand(state) : null) ??
      selectCommand(state);
    if (command) host.send(command);
    // A machine just picked up should face the way it already faces, so `r`
    // turns it from where it stands rather than from an arbitrary default.
    if (state.mode.kind === 'relocate' && !state.moving) askedFor = '';
  });

  canvas.addEventListener('pointerup', (event) => {
    const state = buildState();
    if (state.mode.kind !== 'room' || !state.anchor) return;
    if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    const command = pendingCommand(state);
    setBuild({ anchor: null });
    if (command) host.send(command);
    askedFor = '';
  });

  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      // Escape disarms first (a tool in hand → the build shelf, still open),
      // then leaves build mode; from inspect it resets the camera.
      const current = buildState().mode.kind;
      const next: BuildMode = current === 'inspect' || current === 'build' ? { kind: 'inspect' } : { kind: 'build' };
      setBuild({ mode: next, anchor: null, verdict: null });
      if (current === 'inspect') renderer.resetView();
      askedFor = '';
    }
    if (event.key === 'r' || event.key === 'R') {
      setBuild({ rotated: !buildState().rotated });
      askAbout(buildState());
    }
  });
}

/**
 * Whether the cursor's mode points at *things* rather than at floor — the
 * modes whose click resolves the structure under the pointer (M1.2).
 */
function aimsAtThings(state: BuildState): boolean {
  switch (state.mode.kind) {
    case 'inspect':
    case 'build':
    case 'bulldoze':
      return true;
    // Relocate points at a machine until one is in hand, then at floor.
    case 'relocate':
      return state.moving == null;
    default:
      return false;
  }
}

/** The edit the cursor currently describes, as a command. */
function pendingCommand(state: BuildState): FloorCommand | null {
  // Relocate answers before the rect check: with nothing in hand there is no
  // ghost, and the click falls through to a selection.
  if (state.mode.kind === 'relocate') return relocateCommand(state);
  const rect = pendingRect(state);
  if (!rect) return null;
  switch (state.mode.kind) {
    case 'room':
      return { kind: 'placeRoom', room: state.mode.typeId, x: rect.x, y: rect.y, w: rect.w, h: rect.h };
    case 'tool':
      // The probe and the new-machine click share this shape; a used buy is
      // routed through the campaign at the click so the market can stamp the
      // price (M1.2) — but its legality on the floor is the same question.
      return {
        kind: 'placeTool',
        tool: state.mode.typeId,
        ...(state.mode.modelId != null ? { model: state.mode.modelId } : {}),
        x: rect.x,
        y: rect.y,
        rotated: state.rotated,
      };
    case 'bulldoze':
      return { kind: 'bulldoze', x: rect.x, y: rect.y };
    default:
      return null;
  }
}

/**
 * The machine the relocate cursor has hold of, out of the sim's own selection.
 * A room is not a thing you pick up, so only a tool answers.
 */
function movingTool(selection: FloorUi['site']['selection']): MovingTool | null {
  if (!selection || selection.kind !== 'tool') return null;
  return {
    id: selection.id,
    typeId: selection.typeId,
    name: selection.name,
    x: selection.x,
    y: selection.y,
    w: selection.w,
    h: selection.h,
    rotated: selection.rotated,
  };
}

/**
 * Relocate mode's click: pick a machine up, or — if one is already in hand and
 * the pointer is somewhere else — put it down there.
 *
 * The two-step is what makes this a *move* rather than a drag: the sim owns the
 * selection, so what is in hand is a fact about the floor rather than about the
 * pointer, and a refused destination leaves the machine held rather than
 * dropped.
 */
function relocateCommand(state: BuildState): FloorCommand | null {
  const { cursor, moving } = state;
  if (!cursor) return null;
  if (!moving) return null;
  const onItself =
    cursor.x >= moving.x &&
    cursor.x < moving.x + moving.w &&
    cursor.y >= moving.y &&
    cursor.y < moving.y + moving.h;
  if (onItself) return null;
  return { kind: 'moveTool', id: moving.id, x: cursor.x, y: cursor.y, rotated: state.rotated };
}

/** Inspect mode's click: point the sim's cursor at a cell. */
function selectCommand(state: BuildState): FloorCommand | null {
  if (!state.cursor) return null;
  return { kind: 'select', x: state.cursor.x, y: state.cursor.y };
}

/** Inspect mode's shift-click: expedite the lot at a cell. */
function expediteCommand(state: BuildState): FloorCommand | null {
  if (!state.cursor) return null;
  return { kind: 'expedite', x: state.cursor.x, y: state.cursor.y };
}

/** The ghost the renderer draws under the pointer. */
function ghost(state: BuildState): CursorGhost | null {
  if (state.mode.kind === 'inspect' || state.mode.kind === 'build') return null;
  const rect = pendingRect(state);
  if (!rect) return null;
  const verdict = state.asking ? 'unknown' : state.verdict ? 'refused' : 'legal';
  return { ...rect, verdict };
}

/**
 * Long tasks, where the browser will report them.
 *
 * The frame loop can time itself, but it cannot see the work it does not do:
 * React reconciling an overlay, a garbage collection, a worker message being
 * deserialized. Those land as *long tasks*, and B0.6's budget is about the main
 * thread rather than about the render callback, so this is the instrument that
 * closes the gap. Firefox does not implement the entry type; that is recorded
 * rather than papered over.
 */
function watchLongTasks(): { drain: () => { count: number; worst: number }; supported: boolean } {
  let count = 0;
  let worst = 0;
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        count += 1;
        worst = Math.max(worst, entry.duration);
      }
    });
    observer.observe({ type: 'longtask', buffered: true });
    return {
      supported: true,
      drain: () => {
        const seen = { count, worst };
        count = 0;
        worst = 0;
        return seen;
      },
    };
  } catch {
    return { supported: false, drain: () => ({ count: 0, worst: 0 }) };
  }
}

function loop(host: FloorHost, renderer: FloorRenderer): void {
  // Frame accounting. `performance.now()` is everywhere in here and nowhere in
  // the sim — the whole reason the sim lives behind a fixed-step worker.
  const frames: number[] = [];
  // The whole callback, which is what the budget's "main thread inside half the
  // frame" is actually about — kept apart from `frames`, which measures the
  // *interval* and therefore includes everything the browser does between them.
  const scripts: number[] = [];
  let lastFrameAt = performance.now();
  let lastHudAt = lastFrameAt;
  const longTasks = watchLongTasks();

  // Publish tracking, so interpolation runs on the observed publish period
  // rather than an assumed one: a throttled tab or a slow worker changes it,
  // and pretending otherwise is what makes interpolated motion stutter.
  let lastSeq = 0;
  let lastSeqAt = lastFrameAt;
  let publishPeriod = 100;
  let publishesAtHud = 0;
  // Command traffic, differenced the same way the publishes are.
  let commandsAtHud = 0;
  let commandBytesAtHud = 0;

  const frame = () => {
    requestAnimationFrame(frame);
    const now = performance.now();
    const frameMs = now - lastFrameAt;
    lastFrameAt = now;
    frames.push(frameMs);
    if (frames.length > 120) frames.shift();

    const latest = host.latest();
    const previous = host.previous();
    if (latest && latest.seq !== lastSeq) {
      if (lastSeq !== 0) {
        const observed = now - lastSeqAt;
        // Smoothed, and floored well below a frame so a hitch cannot make the
        // alpha divide by something absurd.
        publishPeriod = Math.max(4, publishPeriod * 0.8 + observed * 0.2);
      }
      lastSeq = latest.seq;
      lastSeqAt = now;
    }
    const alpha = (now - lastSeqAt) / publishPeriod;
    const cost = renderer.render(previous, latest, alpha, ghost(buildState()));
    const scriptMs = performance.now() - now;
    scripts.push(scriptMs);
    if (scripts.length > 120) scripts.shift();

    if (now - lastHudAt >= HUD_MS) {
      const elapsed = now - lastHudAt;
      const publishes = host.publishes();
      const bytes = latest ? latest.byteLen : 0;
      const publishHz = ((publishes - publishesAtHud) * 1000) / elapsed;
      const worker = snapshot().worker;
      const commands = worker?.commands ?? 0;
      const commandBytes = worker?.commandBytes ?? 0;
      const tasks = longTasks.drain();
      update({
        fps: (frames.length * 1000) / frames.reduce((a, b) => a + b, 0),
        frameMs: frames.reduce((a, b) => a + b, 0) / frames.length,
        frameMsP95: percentile(frames, 0.95),
        scriptMs: scripts.reduce((a, b) => a + b, 0) / scripts.length,
        scriptMsP95: percentile(scripts, 0.95),
        renderMs: cost.renderMs,
        boundaryMs: cost.boundaryMs,
        longTasks: ((tasks.count * 1000) / elapsed),
        longestTaskMs: tasks.worst,
        longTasksSupported: longTasks.supported,
        drawn: cost.drawn,
        structures: cost.structures,
        lots: cost.lots,
        heatBytes: latest ? latest.heatW * latest.heatH : 0,
        alpha: cost.alpha,
        lod: cost.lod,
        publishes,
        publishHz,
        bytesPerPublish: bytes,
        bytesPerSecond: Math.round(bytes * publishHz),
        droppedCommands: host.droppedCommands(),
        commandsPerSecond: ((commands - commandsAtHud) * 1000) / elapsed,
        commandBytesPerSecond: ((commandBytes - commandBytesAtHud) * 1000) / elapsed,
      });
      flush();
      publishesAtHud = publishes;
      commandsAtHud = commands;
      commandBytesAtHud = commandBytes;
      lastHudAt = now;
    }
  };

  requestAnimationFrame(frame);
}

/**
 * The campaign's saves (M1.1, docs/13-architecture.md "Saves"): autosave on a
 * cadence in weeks and in wall-clock time, on the tab going dark, and on
 * request; export and import as files the player owns; resume at boot. The
 * bytes are the sim's; this only decides when to ask for them and where they
 * go. Real time appears here — in *when* to save — and nowhere in state.
 */
class SaveKeeper {
  private lastWeek: number | null = null;
  private lastAt = 0;
  private savedAt: number | null = null;
  private store: SaveStore | null = null;
  private error: string | null = null;
  private busy = false;
  private ended = false;

  constructor(private readonly host: FloorHost) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void this.saveNow();
    });
  }

  /** Every tick: is an autosave due? */
  observe(week: number, run: string): void {
    const now = performance.now();
    if (this.lastWeek === null) {
      this.lastWeek = week;
      this.lastAt = now;
      return;
    }
    const weeksDue = week - this.lastWeek >= AUTOSAVE_WEEKS;
    const timeDue = week !== this.lastWeek && now - this.lastAt >= AUTOSAVE_MS;
    const justEnded = run !== 'running' && !this.ended;
    if (justEnded) this.ended = true;
    if (weeksDue || timeDue || justEnded) {
      this.lastWeek = week;
      this.lastAt = now;
      void this.saveNow();
    }
  }

  async resume(): Promise<void> {
    const found = await readAutosave();
    if (!found) return;
    const refusal = await this.host.load(found.json);
    if (refusal) {
      this.error = `autosave not loaded: ${refusal}`;
      console.warn(this.error);
      return;
    }
    this.store = found.store;
    this.savedAt = Date.now();
    noteLoad();
  }

  async saveNow(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      const json = await this.host.save();
      if (json.length === 0) return;
      const store = await writeAutosave(json);
      this.store = store;
      this.error = store === 'none' ? 'no browser storage would take the autosave — export to a file' : null;
      if (store !== 'none') {
        this.savedAt = Date.now();
        noteSave();
      }
    } catch (error) {
      this.error = String(error);
    } finally {
      this.busy = false;
    }
  }

  actions(): CampaignActions {
    return {
      saveNow: () => this.saveNow(),
      exportSave: async () => {
        const json = await this.host.save();
        const week = snapshot().campaign?.date ?? 'campaign';
        downloadSave(json, week);
        noteSave();
      },
      importSave: async () => {
        const json = await pickSaveFile();
        if (!json) return;
        const refusal = await this.host.load(json);
        if (refusal) {
          this.error = `import refused: ${refusal}`;
          window.alert(this.error);
          return;
        }
        this.error = null;
        noteLoad();
        await this.saveNow();
      },
      newCampaign: async () => {
        if (!window.confirm('Start a new campaign? The autosave is cleared; export first if you want to keep it.')) return;
        await clearAutosave();
        const url = new URL(window.location.href);
        url.searchParams.set('fresh', '1');
        window.location.href = url.toString();
      },
      autosave: () => ({ store: this.store, at: this.savedAt, error: this.error }),
    };
  }
}

/** Nearest-rank percentile over an unsorted sample. */
function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))] ?? 0;
}
